import { Stack, Typography } from '@mui/material'
import { EnhancedDomain, type Model } from 'higgins-domain'
import { memo, useEffect, useState } from 'react'
import type { Object3D, Scene } from 'three'

import type { CanvasSystem } from '@salsita-ai/platform-project-sdk/configurator'
import {
  AttributeSwitchSelector,
  useAccessService,
  usePatientObservableValue,
} from '@salsita-ai/platform-project-sdk/react'
import { PropertyContainer, SummaryItem } from '@salsita-ai/platform-project-sdk/ui'

import { useRenderObjectThumbnail } from '../hooks/useRenderObjectThumbnail.js'
import { configuratorInterface } from '../systems/configurator-interface.js'

const capitalizeFirst = (s: string) => (s.length > 0 ? s.charAt(0).toUpperCase() + s.slice(1) : s)

// Hook to track when first load has finished + 500ms delay
const useFirstLoadFinishedWithDelay = (canvasSystem: CanvasSystem<Model.AnyDomainEntity>) => {
  const [readyForThumbnails, setReadyForThumbnails] = useState(false)
  const firstLoadFinished = usePatientObservableValue(canvasSystem.firstLoadFinished$, false)

  useEffect(() => {
    if (firstLoadFinished && !readyForThumbnails) {
      setReadyForThumbnails(true)
    }
    return undefined
  }, [firstLoadFinished, readyForThumbnails])

  return readyForThumbnails
}

const SegmentSummary = memo(
  ({
    scene,
    productCode,
    productName,
    colorName,
    count,
    price,
    representativeEntity,
    showPrices,
  }: {
    readonly scene: Scene | undefined
    readonly productCode: string
    readonly productName: string
    readonly colorName: string
    readonly count: number
    readonly price: number
    readonly representativeEntity: EnhancedDomain.$Segment
    readonly showPrices: boolean
  }) => {
    const [thumbnailUrl, setThumbnailUrl] = useState<string | undefined>(undefined)
    const { canvasSystem } = configuratorInterface.useConfigurator()
    const { render } = useRenderObjectThumbnail(scene)
    const readyForThumbnails = useFirstLoadFinishedWithDelay(canvasSystem)

    useEffect(() => {
      if (!scene || !readyForThumbnails) {
        return
      }

      const generate = async () => {
        try {
          const mesh = await waitForObjectByName(canvasSystem, representativeEntity.id)
          if (!mesh) {
            return
          }
          const objects: Object3D[] = [mesh]
          if (EnhancedDomain.$BaseCabinet.is(representativeEntity)) {
            const matchingCountertop = representativeEntity.$matchingCountertop
            if (matchingCountertop) {
              const counterTop = await waitForObjectByName(canvasSystem, matchingCountertop.id)
              if (counterTop) objects.push(counterTop)
            }
          }
          if (!render) {
            return
          }
          const blob = await render(objects, {
            width: 320,
            height: 240,
            padding: 1,
          })
          const url = URL.createObjectURL(blob)
          setThumbnailUrl(prev => {
            if (prev) URL.revokeObjectURL(prev)
            return url
          })
        } catch (err) {
          console.error('Failed to generate thumbnail for segment summary:', err)
        }
      }

      void generate()
    }, [scene, readyForThumbnails, representativeEntity, canvasSystem, render])

    useEffect(() => {
      return () => {
        if (thumbnailUrl) {
          URL.revokeObjectURL(thumbnailUrl)
        }
      }
    }, [thumbnailUrl])

    return (
      <SummaryItem
        title={productName ? capitalizeFirst(productName) : productCode}
        subtitle={`${productCode} · ${colorName} · x${count}`}
        secondarySlot={showPrices ? <Typography variant="body2">${price.toFixed(2)}</Typography> : undefined}
        thumbnailUrl={thumbnailUrl}
      />
    )
  }
)

export const Summary = () => {
  const { canvasSystem } = configuratorInterface.useConfigurator()
  const $scene = configuratorInterface.useRootEntityState()
  const accessService = useAccessService()
  const ready = usePatientObservableValue(canvasSystem.ready$, null)
  const scene = ready?.scene

  const isViewMode = accessService.accessInfo.viewMode
  const items = $scene.$productGroups.map(group => (
    <SegmentSummary
      key={group.productCode}
      scene={scene}
      productCode={group.productCode}
      productName={group.productName}
      colorName={group.colorName}
      count={group.count}
      price={group.price}
      representativeEntity={group.representativeEntity}
      showPrices={$scene.showPrices && !isViewMode}
    />
  ))

  return (
    <PropertyContainer.Section title="Summary">
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <Typography variant="h6">Items</Typography>
        {!isViewMode && <AttributeSwitchSelector entityId={$scene.id} instanceName="showPrices" title="" />}
      </Stack>
      {items}
    </PropertyContainer.Section>
  )
}

const waitForObjectByName = async (
  canvasSystem: CanvasSystem<Model.AnyDomainEntity>,
  id: Model.AnyDomainEntity['id'],
  retries = 3,
  delayMs = 100
): Promise<Object3D | null> => {
  for (let i = 0; i <= retries; i++) {
    const obj = canvasSystem.findEntity3d({ entityIds: [id], entityRoots: true })?.[0]
    if (obj) return obj
    await new Promise(resolve => setTimeout(resolve, delayMs))
  }
  return null
}
